import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { usePrevious } from '@/Components/ContentListView/Calendar/usePrevious';
import { ElementIds } from '@/Constants/ElementIDs';
import { log, LoggingDomain } from '@/Logging';
import { EditorEventSource } from '@/Types/EditorEventSource';
import { classNames } from '@standardnotes/utils';
import { useResponsiveEditorFontSize } from '@/Utils/getPlaintextFontSize';
import { ApplicationEvent, EditorFontSize, isPayloadSourceRetrieved, WebAppEvent, PrefDefaults, LocalPrefKey, } from '@standardnotes/snjs';
import { isIOS, TAB_COMMAND } from '@standardnotes/ui-services';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { findStringMatches } from '../UniversalSearch/findStringMatches';
import { PlainEditorSearchBackdrop } from './search/PlainEditorSearchBackdrop';
import { buildPlainSearchHighlightHtml } from './search/buildPlainSearchHighlightHtml';
import { replaceAllTextRangesWithUndoSupport, replaceTextRangeWithUndoSupport } from './replacePlainTextWithUndoSupport';
import { scrollPlainTextareaToOffset } from './search/scrollPlainTextareaToOffset';
export const PlainEditor = forwardRef(({ application, spellcheck, controller, locked, onFocus, onBlur, onTextChange, isSearchMode, search }, ref) => {
    const [editorText, setEditorText] = useState();
    const [textareaUnloading, setTextareaUnloading] = useState(false);
    const [lineHeight, setLineHeight] = useState();
    const [fontSize, setFontSize] = useState();
    const responsiveFontSize = useResponsiveEditorFontSize(fontSize || EditorFontSize.Normal);
    const previousSpellcheck = usePrevious(spellcheck);
    const lastEditorFocusEventSource = useRef();
    const needsAdjustMobileCursor = useRef(false);
    const isAdjustingMobileCursor = useRef(false);
    const note = useRef(controller.item);
    const [isPendingLocalPropagation, setIsPendingLocalPropagation] = useState(false);
    const tabObserverDisposer = useRef();
    const mutationObserver = useRef(null);
    const textareaRef = useRef(null);
    const textChangeObservers = useRef(new Set());
    const notifyTextChange = useCallback(() => {
        onTextChange === null || onTextChange === void 0 ? void 0 : onTextChange();
        textChangeObservers.current.forEach((observer) => observer());
    }, [onTextChange]);
    const persistText = useCallback((text, options) => {
        setEditorText(text);
        setIsPendingLocalPropagation(true);
        return controller
            .saveAndAwaitLocalPropagation({
            text,
            isUserModified: true,
            bypassDebouncer: options === null || options === void 0 ? void 0 : options.bypassDebouncer,
        })
            .then(() => {
            setIsPendingLocalPropagation(false);
        });
    }, [controller]);
    const focusEditor = useCallback(() => {
        const element = document.getElementById(ElementIds.NoteTextEditor);
        if (element) {
            lastEditorFocusEventSource.current = EditorEventSource.Script;
            element.focus();
        }
    }, []);
    useImperativeHandle(ref, () => ({
        focus() {
            focusEditor();
        },
        getText() {
            var _a, _b, _c;
            return (_c = (_b = (_a = textareaRef.current) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : editorText) !== null && _c !== void 0 ? _c : '';
        },
        getTextarea() {
            return textareaRef.current;
        },
        setSelection(start, end, options) {
            var _a;
            const textarea = textareaRef.current;
            if (!textarea) {
                return;
            }
            const scrollTopBefore = textarea.scrollTop;
            const shouldSelectInEditor = (_a = options === null || options === void 0 ? void 0 : options.selectInEditor) !== null && _a !== void 0 ? _a : !(search === null || search === void 0 ? void 0 : search.isOpen);
            if (!shouldSelectInEditor) {
                if ((options === null || options === void 0 ? void 0 : options.scrollIntoView) !== false) {
                    scrollPlainTextareaToOffset(textarea, start, end);
                }
                else {
                    textarea.scrollTop = scrollTopBefore;
                }
                return;
            }
            textarea.setSelectionRange(start, end);
            if ((options === null || options === void 0 ? void 0 : options.scrollIntoView) !== false) {
                scrollPlainTextareaToOffset(textarea, start, end);
            }
            else {
                textarea.scrollTop = scrollTopBefore;
            }
            if (options === null || options === void 0 ? void 0 : options.focus) {
                lastEditorFocusEventSource.current = EditorEventSource.Script;
                textarea.focus();
            }
        },
        async replaceRange(start, end, replacement) {
            const textarea = textareaRef.current;
            if (!textarea || locked) {
                return;
            }
            const nextText = replaceTextRangeWithUndoSupport(textarea, start, end, replacement);
            void persistText(nextText, { bypassDebouncer: true });
            if (!(search === null || search === void 0 ? void 0 : search.isOpen)) {
                notifyTextChange();
            }
        },
        async replaceAllRanges(ranges, replacement) {
            const textarea = textareaRef.current;
            if (!textarea || locked || ranges.length < 1) {
                return;
            }
            const nextText = replaceAllTextRangesWithUndoSupport(textarea, ranges, replacement);
            void persistText(nextText, { bypassDebouncer: true });
            if (!(search === null || search === void 0 ? void 0 : search.isOpen)) {
                notifyTextChange();
            }
        },
        onTextChange(callback) {
            textChangeObservers.current.add(callback);
            return () => {
                textChangeObservers.current.delete(callback);
            };
        },
    }), [editorText, focusEditor, locked, notifyTextChange, persistText, search === null || search === void 0 ? void 0 : search.isOpen]);
    useEffect(() => {
        return () => {
            var _a, _b;
            (_a = mutationObserver.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            (_b = tabObserverDisposer.current) === null || _b === void 0 ? void 0 : _b.call(tabObserverDisposer);
            tabObserverDisposer.current = undefined;
            mutationObserver.current = null;
        };
    }, []);
    useEffect(() => {
        const disposer = controller.addNoteInnerValueChangeObserver((updatedNote, source) => {
            if (updatedNote.uuid !== note.current.uuid) {
                throw Error('Editor received changes for non-current note');
            }
            if (!isPendingLocalPropagation) {
                if (isPayloadSourceRetrieved(source) ||
                    editorText == undefined ||
                    updatedNote.editorIdentifier !== note.current.editorIdentifier ||
                    updatedNote.noteType !== note.current.noteType) {
                    setEditorText(updatedNote.text);
                    notifyTextChange();
                }
            }
            note.current = updatedNote;
        });
        return disposer;
    }, [
        controller,
        editorText,
        controller.item.uuid,
        controller.item.editorIdentifier,
        controller.item.noteType,
        isPendingLocalPropagation,
        notifyTextChange,
    ]);
    const onTextAreaChange = ({ currentTarget }) => {
        const text = currentTarget.value;
        void persistText(text).then(() => {
            notifyTextChange();
        });
    };
    const onContentFocus = useCallback(() => {
        if (!isAdjustingMobileCursor.current) {
            needsAdjustMobileCursor.current = true;
        }
        application.notifyWebEvent(WebAppEvent.EditorDidFocus, { eventSource: lastEditorFocusEventSource.current });
        lastEditorFocusEventSource.current = undefined;
        onFocus();
    }, [application, isAdjustingMobileCursor, lastEditorFocusEventSource, onFocus]);
    const onContentBlur = useCallback((event) => {
        lastEditorFocusEventSource.current = undefined;
        onBlur(event);
    }, [lastEditorFocusEventSource, onBlur]);
    const scrollMobileCursorIntoViewAfterWebviewResize = useCallback(() => {
        var _a, _b;
        if (needsAdjustMobileCursor.current) {
            needsAdjustMobileCursor.current = false;
            isAdjustingMobileCursor.current = true;
            (_a = document.getElementById('note-text-editor')) === null || _a === void 0 ? void 0 : _a.blur();
            (_b = document.getElementById('note-text-editor')) === null || _b === void 0 ? void 0 : _b.focus();
            isAdjustingMobileCursor.current = false;
        }
    }, [needsAdjustMobileCursor]);
    useEffect(() => {
        const disposer = application.addWebEventObserver((event) => {
            if (event === WebAppEvent.MobileKeyboardWillChangeFrame) {
                scrollMobileCursorIntoViewAfterWebviewResize();
            }
        });
        return disposer;
    }, [application, scrollMobileCursorIntoViewAfterWebviewResize]);
    useEffect(() => {
        var _a;
        const shouldFocus = controller.isTemplateNote && ((_a = controller.templateNoteOptions) === null || _a === void 0 ? void 0 : _a.autofocusBehavior) === 'editor';
        if (shouldFocus) {
            focusEditor();
        }
    }, [controller, focusEditor]);
    const reloadPreferences = useCallback(() => {
        const lineHeight = application.preferences.getLocalValue(LocalPrefKey.EditorLineHeight, PrefDefaults[LocalPrefKey.EditorLineHeight]);
        const fontSize = application.preferences.getLocalValue(LocalPrefKey.EditorFontSize, PrefDefaults[LocalPrefKey.EditorFontSize]);
        setLineHeight(lineHeight);
        setFontSize(fontSize);
    }, [application]);
    useEffect(() => {
        reloadPreferences();
        return application.addEventObserver(async (event) => {
            const events = [ApplicationEvent.PreferencesChanged, ApplicationEvent.LocalPreferencesChanged];
            if (events.includes(event)) {
                reloadPreferences();
            }
        });
    }, [reloadPreferences, application]);
    useEffect(() => {
        if (previousSpellcheck === undefined) {
            return;
        }
        if (spellcheck !== previousSpellcheck) {
            setTextareaUnloading(true);
            setTimeout(() => {
                setTextareaUnloading(false);
            }, 0);
        }
    }, [spellcheck, previousSpellcheck]);
    const onRef = useCallback((element) => {
        textareaRef.current = element;
        if (tabObserverDisposer.current || !element) {
            return;
        }
        log(LoggingDomain.NoteView, 'On system editor ref');
        /**
         * Insert 4 spaces when a tab key is pressed, only used when inside of the text editor.
         * If the shift key is pressed first, this event is not fired.
         */
        const editor = document.getElementById(ElementIds.NoteTextEditor);
        if (!editor) {
            console.error('Editor is not yet mounted; unable to add tab observer.');
            return;
        }
        tabObserverDisposer.current = application.keyboardService.addCommandHandler({
            element: editor,
            command: TAB_COMMAND,
            onKeyDown: (event) => {
                if (document.hidden || note.current.locked || event.shiftKey) {
                    return;
                }
                event.preventDefault();
                /** Using document.execCommand gives us undo support */
                const insertSuccessful = document.execCommand('insertText', false, '\t');
                if (!insertSuccessful) {
                    /** document.execCommand works great on Chrome/Safari but not Firefox */
                    const start = editor.selectionStart || 0;
                    const end = editor.selectionEnd || 0;
                    const spaces = '    ';
                    /** Insert 4 spaces */
                    editor.value = editor.value.substring(0, start) + spaces + editor.value.substring(end);
                    /** Place cursor 4 spaces away from where the tab key was pressed */
                    editor.selectionStart = editor.selectionEnd = start + 4;
                }
                void persistText(editor.value).then(() => {
                    notifyTextChange();
                });
            },
        });
        const observer = new MutationObserver((records) => {
            for (const record of records) {
                record.removedNodes.forEach((node) => {
                    var _a;
                    if (node.isEqualNode(editor)) {
                        (_a = tabObserverDisposer.current) === null || _a === void 0 ? void 0 : _a.call(tabObserverDisposer);
                        tabObserverDisposer.current = undefined;
                        observer.disconnect();
                    }
                });
            }
        });
        observer.observe(editor.parentElement, { childList: true });
        mutationObserver.current = observer;
    }, [application.keyboardService, notifyTextChange, persistText]);
    const { isOpen: searchIsOpen = false, query: searchQuery = '', isCaseSensitive: searchIsCaseSensitive = false, shouldHighlightAll: searchShouldHighlightAll = false, activeMatch: searchActiveMatch, } = search !== null && search !== void 0 ? search : {};
    const searchHighlightHtml = useMemo(() => {
        if (!searchIsOpen || !searchQuery) {
            return null;
        }
        const text = editorText !== null && editorText !== void 0 ? editorText : '';
        const matches = findStringMatches(text, searchQuery, searchIsCaseSensitive);
        return buildPlainSearchHighlightHtml({
            isOpen: true,
            query: searchQuery,
            results: matches.map((range) => ({ id: `plain-${range.start}`, payload: range })),
            currentResult: searchActiveMatch ? { id: 'active', payload: searchActiveMatch } : undefined,
            shouldHighlightAll: searchShouldHighlightAll,
        }, () => text);
    }, [editorText, searchIsOpen, searchQuery, searchIsCaseSensitive, searchShouldHighlightAll, searchActiveMatch]);
    if (textareaUnloading) {
        return null;
    }
    const editorTypographyClassName = classNames(lineHeight && `leading-${lineHeight.toLowerCase()}`, responsiveFontSize);
    const textareaClassName = classNames('editable font-editor flex-grow', editorTypographyClassName, searchHighlightHtml != null && 'plain-editor-with-search-highlights', isIOS() && '!pb-12');
    const textareaElement = (_jsx("textarea", { autoComplete: "off", dir: "auto", id: ElementIds.NoteTextEditor, onChange: onTextAreaChange, onFocus: onContentFocus, onBlur: onContentBlur, readOnly: locked, ref: onRef, spellCheck: spellcheck, value: editorText, className: textareaClassName }));
    if (!isSearchMode) {
        return textareaElement;
    }
    return (_jsxs("div", { className: "plain-editor-search-container relative flex min-h-0 flex-grow flex-col", children: [searchHighlightHtml != null && (_jsx(PlainEditorSearchBackdrop, { html: searchHighlightHtml, textareaRef: textareaRef, typographyClassName: editorTypographyClassName, syncDependency: editorText })), textareaElement] }));
});
